'use client';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';

interface BeforeAfterSliderProps {
  beforeSrc: string;
  afterSrc: string;
  beforeAlt: string;
  afterAlt: string;
  initialPosition?: number;
}

export default function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeAlt,
  afterAlt,
  initialPosition = 45,
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(initialPosition);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPosition((x / rect.width) * 100);
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    updatePosition(e.clientX);
  };
  const onTouchStart = (e: React.TouchEvent) => {
    setDragging(true);
    updatePosition(e.touches[0].clientX);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => dragging && updatePosition(e.clientX);
    const onTouchMove = (e: TouchEvent) => dragging && updatePosition(e.touches[0].clientX);
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, updatePosition]);

  return (
    <div
      ref={containerRef}
      className="ba-slider w-full"
      style={{ aspectRatio: '16/9', cursor: dragging ? 'ew-resize' : 'ew-resize' }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      role="slider"
      aria-label="Before and after comparison slider"
      aria-valuenow={Math.round(position)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {/* Before image (full width) */}
      <div className="absolute inset-0">
        <AppImage src={beforeSrc} alt={beforeAlt} fill className="object-cover" />
        {/* Before label */}
        <div
          className="absolute top-4 left-4"
          style={{
            background: 'rgba(13,13,13,0.75)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '9999px',
            padding: '4px 12px',
            fontSize: '11px',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'rgba(212,212,216,0.8)',
          }}
        >
          Before
        </div>
      </div>

      {/* After image (clipped) */}
      <div
        className="ba-after"
        style={{ width: `${position}%` }}
      >
        <AppImage
          src={afterSrc}
          alt={afterAlt}
          fill
          className="object-cover"
          style={{ width: `${100 / (position / 100)}%`, maxWidth: 'none' }}
        />
        {/* After label */}
        <div
          className="absolute top-4 right-4"
          style={{
            background: 'rgba(59,130,246,0.85)',
            backdropFilter: 'blur(8px)',
            borderRadius: '9999px',
            padding: '4px 12px',
            fontSize: '11px',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'white',
          }}
        >
          After
        </div>
      </div>

      {/* Handle */}
      <div
        className="ba-handle"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      >
        <div className="ba-handle-icon">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
            <path d="M8 5l-5 7 5 7M16 5l5 7-5 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
}